import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminService } from "../api/AdminService";
import ConfirmDestructiveModal from "../components/ConfirmDestructiveModal";

const AdminIngestionPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const data = await AdminService.getIngestionStatus();
      setStatus(data);
    } catch (error) {
      toast.error("Failed to load ingestion status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleReset = async () => {
    setResetting(true);
    try {
      const result = await AdminService.resetIngestionCollection(resetTarget);
      toast.success(`Reset ${resetTarget}: ${result.rows_deleted} rows cleared`);
      setResetTarget(null);
      loadStatus();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to reset collection",
      );
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold">Ingestion status</h2>
        <button className="btn btn-outline btn-sm" onClick={loadStatus}>
          Refresh
        </button>
      </div>

      {loading || !status ? (
        <p>Loading...</p>
      ) : (
        <>
          <p className="mb-3 text-sm text-base-content/70">
            Total indexed records across all collections: <b>{status.total}</b>
          </p>
          <div className="overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>Vector collection (Weaviate)</th>
                  <th>Record count</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(status.collections).map(([name, count]) => (
                  <tr key={name}>
                    <td className="font-mono text-sm">{name}</td>
                    <td>{count}</td>
                    <td>
                      <button
                        className="btn btn-error btn-outline btn-xs"
                        onClick={() => setResetTarget(name)}
                      >
                        Reset
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {status.reference_tables && (
            <>
              <h3 className="mb-2 mt-6 text-lg font-semibold">
                Reference data (Postgres)
              </h3>
              <p className="mb-3 text-sm text-base-content/70">
                Row counts for the FDA reference tables loaded by the last
                data refresh (initialize_with_data). Read-only.
              </p>
              <div className="overflow-x-auto">
                <table className="table table-zebra">
                  <thead>
                    <tr>
                      <th>Table</th>
                      <th>Row count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(status.reference_tables).map(
                      ([label, count]) => (
                        <tr key={label}>
                          <td>{label}</td>
                          <td>{count}</td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      <ConfirmDestructiveModal
        open={!!resetTarget}
        title="Reset collection"
        description="This clears the record-manager bookkeeping for this collection so it can be fully re-ingested from scratch on the next data refresh. This does not delete the underlying vector data immediately, but it will be treated as new on the next initialize_with_data run. This action is logged."
        confirmText={resetTarget || ""}
        confirmLabel="Reset collection"
        busy={resetting}
        onConfirm={handleReset}
        onCancel={() => setResetTarget(null)}
      />
    </div>
  );
};

export default AdminIngestionPage;
